import { supabase } from '../lib/supabase'

// ── Palette ──
const C = {
  navy:    'FF1C2B3A', navyLight: 'FF2E3F52',
  green:   'FF1A7A4A', greenBg:  'FFF0FFF4',
  red:     'FFA32D2D', redBg:    'FFFFF0F0',
  amber:   'FFB06D00', amberBg:  'FFFFFBEB',
  white:   'FFFFFFFF', light:    'FFF8F9FA',
  border:  'FFD0D5DD', subhdr:   'FFE8F0F7',
  darkTx:  'FF1C1A18', mutedTx:  'FF6B7280',
  purple:  'FF7C3AED', pink:     'FFDB2777',
  totRow:  'FF1C2B3A', altRow:   'FFF5F7FA',
}

const numFmt = '#,##0'
const decFmt = '0.00'
const pctFmt = '0.0%'

const fill  = a => ({ type:'pattern', pattern:'solid', fgColor:{ argb:a } })
const fnt   = (bold=false, sz=10, argb=C.darkTx) => ({ name:'Calibri', size:sz, bold, color:{ argb } })
const bdr   = (s='thin') => { const x={style:s,color:{argb:C.border}}; return {top:x,left:x,bottom:x,right:x} }
const bdrB  = () => { const t={style:'thin',color:{argb:C.border}},b={style:'medium',color:{argb:C.navy}}; return {top:t,left:t,bottom:b,right:t} }
const aln   = (h='left',v='middle') => ({ horizontal:h, vertical:v, wrapText:false })

function sHdr(row, bg=C.navy, fg=C.white, sz=10){
  row.height=22
  row.eachCell(c=>{ c.fill=fill(bg); c.font=fnt(true,sz,fg); c.border=bdr(); c.alignment=aln('center') })
}
function sData(row, light=false){
  row.height=17
  row.eachCell(c=>{ c.fill=fill(light?C.altRow:C.white); c.font=fnt(false,10,C.darkTx); c.border=bdr(); c.alignment=aln() })
}
function sTot(row, bg=C.totRow){
  row.height=22
  row.eachCell(c=>{ c.fill=fill(bg); c.font=fnt(true,10,C.white); c.border=bdrB(); c.alignment=aln() })
}
function sSubHdr(row, bg=C.subhdr){
  row.height=19
  row.eachCell(c=>{ c.fill=fill(bg); c.font=fnt(true,10,C.navyLight); c.border=bdr(); c.alignment=aln() })
}
function nFmt(row, cols, fmt=numFmt){ cols.forEach(c=>{ if(typeof row.getCell(c).value==='number') row.getCell(c).numFmt=fmt }) }
function vColor(row, col, val){ row.getCell(col).font=fnt(true,10, val<0?C.red: val>0?C.green: C.darkTx) }
function setW(ws, widths){ ws.columns=widths.map(w=>({width:w})) }

function addTitle(ws, text){
  ws.mergeCells('A1:L1')
  const c=ws.getCell('A1')
  c.value=text; c.fill=fill(C.navy); c.font=fnt(true,15,C.white); c.alignment=aln('center','middle')
  ws.getRow(1).height=38
}
function addInfo(ws, proj, date){
  ws.mergeCells('A2:L2')
  const c=ws.getCell('A2')
  c.value=`Project: ${proj.name}  |  Code: ${proj.code||'—'}  |  Client: ${proj.organizations?.name||'—'}  |  Date: ${date}  |  QUBEX Cost Intelligence`
  c.font=fnt(false,9,C.mutedTx); c.alignment=aln('center')
  ws.getRow(2).height=15; ws.getRow(3).height=5
}
function sectionHdr(ws, rowNum, text, bg=C.green){
  ws.mergeCells(`A${rowNum}:L${rowNum}`)
  const r=ws.getRow(rowNum)
  r.getCell(1).value=text; r.getCell(1).fill=fill(bg); r.getCell(1).font=fnt(true,10,C.white)
  r.getCell(1).alignment=aln('left','middle'); r.height=20
}

const p=(a,b)=>b>0?a/b:0
const f=(n)=>Number(n||0)

// ══════════════════════════════════════════════════════
export async function generateFullExcelReport(project, dashData, extras){
// ══════════════════════════════════════════════════════
  const ExcelJS=(await import('exceljs')).default
  const wb=new ExcelJS.Workbook()
  wb.creator='QUBEX Cost Intelligence'; wb.created=new Date()

  const pid = project.id
  const cur = project.currency||'SAR'
  const now = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})
  const {d,prog,invoiced,ev,cpi,eac,gp,gpPct,unbilled}=extras
  const vac=d.totalBudget-eac
  const ctc=Math.max(eac-d.totalActual,0)

  // ── Fetch ALL data ──
  const [
    budgetRes, indirectRes, ipcCertRes, ipcLineRes,
    poRes, poLineRes, subRes, storeReceiptRes, storeIssueRes,
    payrollRes, eqpRes, eacRes, finRes, cashInRes, cashOutRes,
    pcRes, accrualRes
  ] = await Promise.all([
    supabase.from('budget_summary').select('*').eq('project_id',pid).order('sort_order',{ascending:true}),
    supabase.from('indirect_budget_items').select('*').eq('project_id',pid).order('sort_order',{ascending:true}),
    supabase.from('ipc_certificates').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('ipc_line_items').select('*').order('created_at',{ascending:true}),
    supabase.from('purchase_orders').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('po_line_items').select('*').order('created_at',{ascending:true}),
    supabase.from('subcontracts').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('store_receipts').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('store_issues').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('payroll_entries').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('equipment_costs').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('forecast_eac').select('*').eq('project_id',pid),
    supabase.from('finance_transactions').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('cash_in_entries').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('cash_out_summary').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
    supabase.from('petty_cash_vouchers').select('*').eq('project_id',pid).order('date_of_voucher',{ascending:false}),
    supabase.from('accrual_entries').select('*').eq('project_id',pid).order('created_at',{ascending:false}),
  ])

  const budget    = budgetRes.data     ||[]
  const indirect  = indirectRes.data   ||[]
  const certs     = ipcCertRes.data    ||[]
  const ipcLines  = ipcLineRes.data    ||[]
  const pos       = poRes.data         ||[]
  const poLines   = poLineRes.data     ||[]
  const subs      = subRes.data        ||[]
  const receipts  = storeReceiptRes.data||[]
  const issues    = storeIssueRes.data ||[]
  const payroll   = payrollRes.data    ||[]
  const equip     = eqpRes.data        ||[]
  const eacData   = eacRes.data        ||[]
  const finance   = finRes.data        ||[]
  const cashIn    = cashInRes.data     ||[]
  const cashOut   = cashOutRes.data    ||[]
  const pc        = pcRes.data         ||[]
  const accruals  = accrualRes.data    ||[]

  // pre-compute eac by sub_code
  const eacBySub={}
  eacData.forEach(r=>{ if(r.sub_code) eacBySub[r.sub_code.toLowerCase().trim()]=(eacBySub[r.sub_code.toLowerCase().trim()]||0)+f(r.eac_amount) })

  // ════════════════════════════════════════════════════════
  // SHEET 1 — EXECUTIVE SUMMARY
  // ════════════════════════════════════════════════════════
  const s1=wb.addWorksheet('1-Executive Summary')
  setW(s1,[30,18,18,18,18,14,20,12,12,12,12,12])
  addTitle(s1,'QUBEX — EXECUTIVE SUMMARY REPORT'); addInfo(s1,project,now)

  // KPI Cards block
  sectionHdr(s1,4,'KEY PERFORMANCE INDICATORS')
  const kpiHdr=s1.getRow(5)
  kpiHdr.values=['KPI','Budget ('+cur+')','Actual ('+cur+')','EAC ('+cur+')','Variance','% Budget Used','Status','','','','','']
  sHdr(kpiHdr); kpiHdr.height=20

  const kpiRows=[
    ['Contract Value',       f(d.cv),              '',                   f(d.cv),    '',               p(d.cv,d.cv),           cur],
    ['Total Budget',         f(d.totalBudget),      '',                   '',         '',               p(d.totalBudget,d.cv),  `${(p(d.totalBudget,d.cv)*100).toFixed(1)}% of CV`],
    ['  Direct Budget',      f(d.directBudget),     '',                   '',         '',               p(d.directBudget,d.totalBudget), ''],
    ['  Indirect Budget',    f(d.indirectBudget),   '',                   '',         '',               p(d.indirectBudget,d.totalBudget),''],
    ['Actual Cost (Total)',  f(d.totalBudget),      f(d.totalActual),     '',         f(d.totalBudget-d.totalActual), p(d.totalActual,d.totalBudget), ''],
    ['  IPC / Subcontracts', '',                    f(d.ipcActual),       '',         '',               p(d.ipcActual,d.totalActual),   ''],
    ['  Store / Materials',  '',                    f(d.storeActual),     '',         '',               p(d.storeActual,d.totalActual), ''],
    ['  Payroll',            '',                    f(d.payActual),       '',         '',               p(d.payActual,d.totalActual),   ''],
    ['  Equipment',          '',                    f(d.eqpActual),       '',         '',               p(d.eqpActual,d.totalActual),   ''],
    ['  Petty Cash',         '',                    f(d.pcActual||0),     '',         '',               p(d.pcActual||0,d.totalActual), ''],
    ['Committed (POs)',      '',                    '',                   f(d.committed),'',            p(d.committed,eac),             `${(p(d.committed,eac)*100).toFixed(1)}% of EAC`],
    ['EAC Forecast',         f(d.totalBudget),      '',                   f(eac),     f(vac),           p(eac,d.totalBudget),           eac>d.totalBudget?'⚠ Over Budget':eac<d.totalBudget?'✓ Under Budget':'= Within Budget'],
    ['Variance at Completion','',                   '',                   '',         f(vac),           p(Math.abs(vac),d.totalBudget), vac>=0?'✓ Saving':'⚠ Overrun'],
    ['Cost to Complete (CTC)','',                   '',                   '',         f(ctc),           p(d.totalActual,eac),           ''],
    ['Earned Value (EV)',    '',                    f(ev),                '',         f(ev-d.totalActual),p(ev,d.totalBudget),          ''],
    ['CPI (EV÷AC)',          '',                    '',                   '',         '',               f(cpi),                         cpi>=1?'✓ Efficient':'⚠ Cost Overrun'],
    ['Progress (Manual)',    '',                    '',                   '',         '',               f(prog)/100,                    '%'],
    ['Cash In',              '',                    f(d.cashIn),          '',         '',               '',                             'Received from client'],
    ['Cash Out',             '',                    f(d.cashOut),         '',         '',               '',                             'Payments made'],
    ['Net Cash',             '',                    f(d.cashIn-d.cashOut),'',         '',               '',                             d.cashIn-d.cashOut>=0?'✓ Positive':'⚠ Deficit'],
    ['Gross Profit',         f(d.cv),               '',                   f(eac),     f(gp),            f(gpPct)/100,                   'CV − EAC'],
  ]
  kpiRows.forEach((row,i)=>{
    const r=s1.getRow(i+6); r.values=[...row,'','','','','']
    const isIndent=String(row[0]).startsWith('  ')
    if(isIndent){ sData(r,i%2===0); r.getCell(1).font=fnt(false,9,C.mutedTx) }
    else sData(r,i%2===0)
    ;[2,3,4,5].forEach(ci=>{ if(typeof r.getCell(ci).value==='number'&&Math.abs(r.getCell(ci).value)>1) r.getCell(ci).numFmt=numFmt })
    r.getCell(6).numFmt=pctFmt
    if(typeof row[4]==='number') vColor(r,5,row[4])
    if(i===15) r.getCell(6).numFmt=decFmt // CPI
    r.height=17
  })
  const s1Tot=s1.getRow(6+kpiRows.length)
  s1Tot.values=['GRAND TOTAL',f(d.totalBudget),f(d.totalActual),f(eac),f(vac),p(d.totalActual,d.totalBudget),vac<0?'⚠ Over Budget':'✓ Under Budget','','','','','']
  sTot(s1Tot); ;[2,3,4,5].forEach(ci=>s1Tot.getCell(ci).numFmt=numFmt); s1Tot.getCell(6).numFmt=pctFmt
  s1Tot.getCell(5).font=fnt(true,10,vac<0?'FFFF9999':'FF90EE90')

  // ════════════════════════════════════════════════════════
  // SHEET 2 — COST CONTROL (Full detail)
  // ════════════════════════════════════════════════════════
  const s2=wb.addWorksheet('2-Cost Control')
  setW(s2,[13,35,16,16,14,14,14,14,14,16,14,14,14,14,16,16])
  addTitle(s2,'COST CONTROL REPORT'); addInfo(s2,project,now)

  const s2Hdr=s2.getRow(4)
  s2Hdr.values=['Sub Code','Description','BAC ('+cur+')','Commitment','Accrual','Inv. Materials','Inv. Subcontr.','Inv. Equipment','Petty Cash','Total Actual','This Period','Cash Out','EAC','Prev EAC','Var EAC','Var@Comp']
  sHdr(s2Hdr)

  // Build eac map by budget_row_id
  const eacByRowId={}
  eacData.forEach(r=>{ if(r.budget_row_id) eacByRowId[r.budget_row_id]=(eacByRowId[r.budget_row_id]||0)+f(r.eac_amount) })

  // IPC actual by sub_code
  const ipcBySub={}
  const certIds=new Set(certs.filter(c=>['approved','paid'].includes(c.status)).map(c=>c.id))
  ipcLines.filter(l=>certIds.has(l.ipc_certificate_id||l.ipc_id)).forEach(l=>{
    const sc=(l.sub_code||'').toLowerCase().trim()
    if(sc) ipcBySub[sc]=(ipcBySub[sc]||0)+f(l.invoiced_amount)
  })
  // Store by sub_code
  const storeBySub={}
  issues.forEach(i=>{ const sc=(i.sub_code||'').toLowerCase().trim(); if(sc) storeBySub[sc]=(storeBySub[sc]||0)+f(i.qty_issued)*f(i.unit_rate) })
  // Petty cash by sub_code
  const pcBySub={}
  pc.forEach(r=>{ const sc=(r.sub_code||'').toLowerCase().trim(); if(sc) pcBySub[sc]=(pcBySub[sc]||0)+f(r.amount_exc_vat) })

  let ccRow=5
  // Direct section
  sectionHdr(s2,ccRow,'DIRECT WORKS — Budget & Cost Control'); ccRow++
  const ccTotDirect={bac:0,commit:0,acc:0,mat:0,ipc:0,eqp:0,pc2:0,actual:0,period:0,cashout:0,eac:0,var:0}

  budget.forEach((row,i)=>{
    const isT=(row.row_type||'').toLowerCase()==='total'
    const sc=(row.sub_code||'').toLowerCase().trim()
    const bac=f(row.total_budget)
    const ipcA=ipcBySub[sc]||0
    const matA=storeBySub[sc]||0
    const pcA=pcBySub[sc]||0
    const totalAct=ipcA+matA+pcA
    const eacV=eacBySub[sc]||0
    const varV=bac-eacV

    const r=s2.getRow(ccRow++)
    r.values=[row.sub_code,row.description||row.package_name,bac,0,0,matA,ipcA,0,pcA,totalAct,0,0,eacV||'',0,eacV?(bac-eacV):'',bac?varV:'']
    if(isT){ sTot(r,'FF2E3F52') } else { sData(r,i%2===0) }
    ;[3,4,5,6,7,8,9,10,11,12,13,14,15,16].forEach(ci=>{ if(typeof r.getCell(ci).value==='number'&&r.getCell(ci).value!==0) r.getCell(ci).numFmt=numFmt })
    if(eacV&&bac){ vColor(r,15,varV); vColor(r,16,varV) }
    r.height=17

    if(!isT){
      ccTotDirect.bac+=bac; ccTotDirect.mat+=matA; ccTotDirect.ipc+=ipcA
      ccTotDirect.pc2+=pcA; ccTotDirect.actual+=totalAct; ccTotDirect.eac+=eacV
    }
  })
  const s2DirTot=s2.getRow(ccRow++)
  s2DirTot.values=['','DIRECT SUB-TOTAL',ccTotDirect.bac,0,0,ccTotDirect.mat,ccTotDirect.ipc,0,ccTotDirect.pc2,ccTotDirect.actual,0,0,ccTotDirect.eac,0,ccTotDirect.bac-ccTotDirect.eac,ccTotDirect.bac-ccTotDirect.eac]
  sTot(s2DirTot); ;[3,6,7,8,9,10,13,15,16].forEach(ci=>s2DirTot.getCell(ci).numFmt=numFmt)
  vColor(s2DirTot,15,ccTotDirect.bac-ccTotDirect.eac); s2DirTot.height=20

  // gap row
  s2.getRow(ccRow++).height=6

  // Indirect section
  sectionHdr(s2,ccRow,'INDIRECT WORKS — Budget & Cost Control'); ccRow++
  const ccTotIndir={bac:0,commit:0,eqp:0,pay:0,pc2:0,actual:0,eac:0}

  indirect.forEach((row,i)=>{
    const isT=(row.row_type||'').toLowerCase()==='total'
    const sc=(row.sub_code||'').toLowerCase().trim()
    const bac=f(row.total_budget)
    const pcA=pcBySub[sc]||0
    const eacV=eacBySub[sc]||0
    const varV=bac-eacV

    const r=s2.getRow(ccRow++)
    r.values=[row.sub_code,row.description||row.package_name,bac,0,0,0,0,0,pcA,pcA,0,0,eacV||'',0,eacV?(bac-eacV):'',bac?varV:'']
    if(isT){ sTot(r,'FF2E3F52') } else { sData(r,i%2===0) }
    ;[3,9,10,13,15,16].forEach(ci=>{ if(typeof r.getCell(ci).value==='number'&&r.getCell(ci).value!==0) r.getCell(ci).numFmt=numFmt })
    if(eacV&&bac){ vColor(r,15,varV); vColor(r,16,varV) }
    r.height=17

    if(!isT){ ccTotIndir.bac+=bac; ccTotIndir.pc2+=pcA; ccTotIndir.actual+=pcA; ccTotIndir.eac+=eacV }
  })
  const s2IndTot=s2.getRow(ccRow++)
  s2IndTot.values=['','INDIRECT SUB-TOTAL',ccTotIndir.bac,0,0,0,0,0,ccTotIndir.pc2,ccTotIndir.actual,0,0,ccTotIndir.eac,0,ccTotIndir.bac-ccTotIndir.eac,ccTotIndir.bac-ccTotIndir.eac]
  sTot(s2IndTot); ;[3,9,10,13,15,16].forEach(ci=>s2IndTot.getCell(ci).numFmt=numFmt)
  vColor(s2IndTot,15,ccTotIndir.bac-ccTotIndir.eac); s2IndTot.height=20

  // Grand Total
  s2.getRow(ccRow++).height=6
  const s2Grand=s2.getRow(ccRow++)
  const grandBac=ccTotDirect.bac+ccTotIndir.bac
  const grandAct=ccTotDirect.actual+ccTotIndir.actual
  const grandEac=ccTotDirect.eac+ccTotIndir.eac
  const grandVar=grandBac-grandEac
  s2Grand.values=['','GRAND TOTAL',grandBac,0,0,ccTotDirect.mat,ccTotDirect.ipc,0,ccTotDirect.pc2+ccTotIndir.pc2,grandAct,0,0,grandEac,0,grandVar,grandVar]
  sTot(s2Grand,'FF1C2B3A'); ;[3,6,7,9,10,13,15,16].forEach(ci=>s2Grand.getCell(ci).numFmt=numFmt)
  s2Grand.getCell(15).font=fnt(true,11,grandVar<0?'FFFF9999':'FF90EE90')
  s2Grand.getCell(16).font=fnt(true,11,grandVar<0?'FFFF9999':'FF90EE90')
  s2Grand.height=24

  // ════════════════════════════════════════════════════════
  // SHEET 3 — IPC CERTIFICATES
  // ════════════════════════════════════════════════════════
  const s3=wb.addWorksheet('3-IPC Certificates')
  setW(s3,[16,22,14,14,16,16,16,14,14,14,14,14])
  addTitle(s3,'IPC CERTIFICATES'); addInfo(s3,project,now)

  const s3Hdr=s3.getRow(4)
  s3Hdr.values=['IPC No.','Description','Issue Date','Due Date','Certified Amount','VAT','Total Inc.VAT','Status','Approval Date','Cumulative','','']
  sHdr(s3Hdr)

  let ipcCum=0
  certs.forEach((cert,i)=>{
    const certLines=ipcLines.filter(l=>(l.ipc_certificate_id||l.ipc_id)===cert.id)
    const certAmt=certLines.reduce((s,l)=>s+f(l.invoiced_amount),0)
    const vat=certAmt*0.15
    ipcCum+=certAmt
    const r=s3.getRow(i+5)
    r.values=[cert.certificate_no||cert.ipc_number||cert.id?.slice(0,8),cert.description||cert.notes||'',cert.issue_date||cert.created_at?.slice(0,10),cert.due_date||'',certAmt,vat,certAmt+vat,cert.status,cert.approved_date||'',ipcCum,'','']
    sData(r,i%2===0)
    ;[5,6,7,10].forEach(ci=>r.getCell(ci).numFmt=numFmt)
    const sc=r.getCell(8)
    if(cert.status==='approved') sc.fill=fill('FFE6F7EE'); sc.font=fnt(false,10,C.green)
    if(cert.status==='paid')     { r.getCell(8).fill=fill('FFF0FFF4'); r.getCell(8).font=fnt(false,10,C.green) }
    if(cert.status==='rejected') r.getCell(8).font=fnt(false,10,C.red)
    if(cert.status==='draft')    r.getCell(8).font=fnt(false,10,C.mutedTx)
    r.height=17
  })
  const s3Tot=s3.getRow(certs.length+5)
  const totalCertAmt=certs.reduce((s,cert)=>{ const cl=ipcLines.filter(l=>(l.ipc_certificate_id||l.ipc_id)===cert.id); return s+cl.reduce((ss,l)=>ss+f(l.invoiced_amount),0)},0)
  s3Tot.values=['TOTAL','','','',totalCertAmt,totalCertAmt*0.15,totalCertAmt*1.15,'','',ipcCum,'','']
  sTot(s3Tot); ;[5,6,7,10].forEach(ci=>s3Tot.getCell(ci).numFmt=numFmt)

  // IPC Line items on same sheet
  s3.getRow(certs.length+7).height=6
  sectionHdr(s3,certs.length+8,'IPC LINE ITEMS DETAIL')
  const s3LHdr=s3.getRow(certs.length+9)
  s3LHdr.values=['IPC No.','Sub Code','Description','Unit','BOQ Qty','This Period Qty','This Amount','Cumulative Qty','Invoiced Amount','','','']
  sHdr(s3LHdr,C.navyLight)
  ipcLines.forEach((line,i)=>{
    const certNo=certs.find(c=>c.id===(line.ipc_certificate_id||line.ipc_id))?.certificate_no||''
    const r=s3.getRow(certs.length+10+i)
    r.values=[certNo,line.sub_code,line.description||'',line.unit,f(line.boq_qty),f(line.this_period_qty),f(line.this_period_amount),f(line.cumulative_qty),f(line.invoiced_amount),'','','']
    sData(r,i%2===0); ;[5,6,7,8,9].forEach(ci=>r.getCell(ci).numFmt=numFmt); r.height=17
  })

  // ════════════════════════════════════════════════════════
  // SHEET 4 — PURCHASE ORDERS
  // ════════════════════════════════════════════════════════
  const s4=wb.addWorksheet('4-Purchase Orders')
  setW(s4,[16,28,14,14,18,18,18,12,14,12,12,12])
  addTitle(s4,'PURCHASE ORDERS'); addInfo(s4,project,now)

  const s4Hdr=s4.getRow(4)
  s4Hdr.values=['PO Number','Supplier / Vendor','Sub Code','Cost Type','Value Exc.VAT','VAT (15%)','Total Inc.VAT','Status','Date','Lines','','']
  sHdr(s4Hdr)

  const poByStatus={approved:0,paid:0,received:0,cancelled:0,draft:0}
  pos.forEach((po,i)=>{
    const amt=f(po.total_exc_vat)
    const vat=amt*0.15
    const r=s4.getRow(i+5)
    r.values=[po.po_number,po.supplier_name||po.vendor_name,po.sub_code||'',po.cost_type||po.type,amt,vat,amt+vat,po.status,po.date||po.created_at?.slice(0,10),poLines.filter(l=>l.po_id===po.id).length,'','']
    sData(r,i%2===0); ;[5,6,7].forEach(ci=>r.getCell(ci).numFmt=numFmt)
    const sc=r.getCell(8)
    if(po.status==='approved'){ sc.fill=fill('FFE6F7EE'); sc.font=fnt(false,10,C.green) }
    if(po.status==='cancelled') sc.font=fnt(false,10,C.red)
    if(po.status==='draft') sc.font=fnt(false,10,C.mutedTx)
    r.height=17
    if(poByStatus[po.status]!==undefined) poByStatus[po.status]+=amt
  })
  const poTotal=pos.reduce((s,p)=>s+f(p.total_exc_vat),0)
  const s4Tot=s4.getRow(pos.length+5)
  s4Tot.values=['TOTAL','','','',poTotal,poTotal*0.15,poTotal*1.15,'','',pos.length,'','']
  sTot(s4Tot); ;[5,6,7].forEach(ci=>s4Tot.getCell(ci).numFmt=numFmt)

  // PO Lines detail
  s4.getRow(pos.length+7).height=6
  sectionHdr(s4,pos.length+8,'PO LINE ITEMS DETAIL')
  const s4LHdr=s4.getRow(pos.length+9)
  s4LHdr.values=['PO Number','Ref','Description','Sub Code','Unit','Qty','Unit Rate','Amount Exc.VAT','','','','']
  sHdr(s4LHdr,C.navyLight)
  poLines.forEach((line,i)=>{
    const poNum=pos.find(p=>p.id===line.po_id)?.po_number||''
    const r=s4.getRow(pos.length+10+i)
    r.values=[poNum,line.ref_code||line.ref,line.description,line.sub_code,line.unit,f(line.quantity||line.qty),f(line.unit_rate),f(line.amount_exc_vat||((line.quantity||line.qty||0)*(line.unit_rate||0))),'','','','']
    sData(r,i%2===0); ;[6,7,8].forEach(ci=>r.getCell(ci).numFmt=numFmt); r.height=17
  })

  // ════════════════════════════════════════════════════════
  // SHEET 5 — SUBCONTRACTS
  // ════════════════════════════════════════════════════════
  const s5=wb.addWorksheet('5-Subcontracts')
  setW(s5,[16,26,14,18,18,18,18,12,14,14,12,12])
  addTitle(s5,'SUBCONTRACTS'); addInfo(s5,project,now)

  const s5Hdr=s5.getRow(4)
  s5Hdr.values=['Contract No.','Subcontractor','Sub Code','Contract Value','Certified','Paid','Outstanding','Status','PO No','Scope','','']
  sHdr(s5Hdr)

  subs.forEach((sub,i)=>{
    const cv2=f(sub.contract_value)
    const cert=f(sub.certified_amount)
    const paid=f(sub.paid_amount)
    const outstanding=cert-paid
    const r=s5.getRow(i+5)
    r.values=[sub.contract_no||sub.subcontract_number,sub.subcontractor_name,sub.sub_code,cv2,cert,paid,outstanding,sub.status,sub.po_number||'',sub.description||sub.scope||'','','']
    sData(r,i%2===0); ;[4,5,6,7].forEach(ci=>r.getCell(ci).numFmt=numFmt)
    const oc=r.getCell(7); oc.font=fnt(false,10,outstanding>0?C.amber:C.green)
    const sc=r.getCell(8)
    if(sub.status==='active') sc.font=fnt(false,10,C.green)
    if(sub.status==='completed') sc.font=fnt(false,10,C.mutedTx)
    r.height=17
  })
  const subCV=subs.reduce((s,x)=>s+f(x.contract_value),0)
  const subCert=subs.reduce((s,x)=>s+f(x.certified_amount),0)
  const subPaid=subs.reduce((s,x)=>s+f(x.paid_amount),0)
  const s5Tot=s5.getRow(subs.length+5)
  s5Tot.values=['TOTAL','','',subCV,subCert,subPaid,subCert-subPaid,'','','','','']
  sTot(s5Tot); ;[4,5,6,7].forEach(ci=>s5Tot.getCell(ci).numFmt=numFmt)

  // ════════════════════════════════════════════════════════
  // SHEET 6 — PAYROLL
  // ════════════════════════════════════════════════════════
  const s6=wb.addWorksheet('6-Payroll')
  setW(s6,[22,14,14,14,14,14,14,14,12,12,12,12])
  addTitle(s6,'PAYROLL'); addInfo(s6,project,now)

  const s6Hdr=s6.getRow(4)
  s6Hdr.values=['Employee Name','Sub Code','Type','Period','Basic Salary','Allowances','Overtime','Deductions','Net Salary','Total Cost','Status','Notes']
  sHdr(s6Hdr)

  payroll.forEach((row,i)=>{
    const r=s6.getRow(i+5)
    r.values=[row.employee_name||row.name,row.sub_code,row.employee_type||row.type,row.month||row.payroll_month||row.period,f(row.basic_salary),f(row.allowances),f(row.overtime_pay||row.overtime),f(row.deductions),f(row.net_salary),f(row.total_cost||row.total_salary),row.status,row.notes||'']
    sData(r,i%2===0); ;[5,6,7,8,9,10].forEach(ci=>r.getCell(ci).numFmt=numFmt)
    if(row.status==='approved') r.getCell(11).font=fnt(false,10,C.green)
    if(row.status==='draft') r.getCell(11).font=fnt(false,10,C.mutedTx)
    r.height=17
  })
  const payNet=payroll.reduce((s,r)=>s+f(r.net_salary),0)
  const payTot=payroll.reduce((s,r)=>s+f(r.total_cost||r.total_salary),0)
  const s6Tot=s6.getRow(payroll.length+5)
  s6Tot.values=['TOTAL','','','','','','','',payNet,payTot,'','']
  sTot(s6Tot); ;[9,10].forEach(ci=>s6Tot.getCell(ci).numFmt=numFmt)

  // ════════════════════════════════════════════════════════
  // SHEET 7 — EQUIPMENT COSTS
  // ════════════════════════════════════════════════════════
  const s7=wb.addWorksheet('7-Equipment Costs')
  setW(s7,[14,14,22,18,14,14,14,16,12,12,12,12])
  addTitle(s7,'EQUIPMENT COSTS'); addInfo(s7,project,now)

  const s7Hdr=s7.getRow(4)
  s7Hdr.values=['EQP ID','Sub Code','Allocation Category','Supplier','PO Number','INV No','Period','Total Exc.VAT','Hours','Status','','']
  sHdr(s7Hdr)

  equip.forEach((row,i)=>{
    const r=s7.getRow(i+5)
    r.values=[row.equipment_id||row.eqp_id,row.sub_code,row.allocation_category||row.category,row.supplier,row.po_number||row.po_id,row.invoice_no||row.inv_no,`${row.year||''}-${String(row.month||'').padStart(2,'0')}`,f(row.invoiced_amount||row.total_exc_vat),f(row.hours),row.status,'','']
    sData(r,i%2===0); ;[8].forEach(ci=>r.getCell(ci).numFmt=numFmt)
    if(row.status==='approved') r.getCell(10).font=fnt(false,10,C.green)
    r.height=17
  })
  const eqpTot=equip.reduce((s,r)=>s+f(r.invoiced_amount||r.total_exc_vat),0)
  const s7Tot=s7.getRow(equip.length+5)
  s7Tot.values=['TOTAL','','','','','','',eqpTot,'','','','']
  sTot(s7Tot); s7Tot.getCell(8).numFmt=numFmt

  // ════════════════════════════════════════════════════════
  // SHEET 8 — STORE (Receipts & Issues)
  // ════════════════════════════════════════════════════════
  const s8=wb.addWorksheet('8-Store')
  setW(s8,[16,18,30,12,12,14,14,14,12,12,12,12])
  addTitle(s8,'STORE — RECEIPTS & ISSUES'); addInfo(s8,project,now)

  sectionHdr(s8,4,'STORE RECEIPTS')
  const s8RHdr=s8.getRow(5)
  s8RHdr.values=['PO Number','Ref Code','Material Description','Unit','Qty Received','Unit Rate','Total Value','Date','Delivery Note','Received By','','']
  sHdr(s8RHdr,C.navyLight)
  receipts.forEach((row,i)=>{
    const r=s8.getRow(i+6)
    const tot=f(row.qty_received)*f(row.unit_rate)
    r.values=[row.po_number||row.po_id,row.ref_code,row.material_description||row.description,row.unit,f(row.qty_received),f(row.unit_rate),tot,row.receipt_date||row.created_at?.slice(0,10),row.delivery_note_no||'',row.received_by||'','','']
    sData(r,i%2===0); ;[5,6,7].forEach(ci=>r.getCell(ci).numFmt=numFmt); r.height=17
  })
  const recTot=receipts.reduce((s,r)=>s+f(r.qty_received)*f(r.unit_rate),0)
  const s8RTot=s8.getRow(receipts.length+6)
  s8RTot.values=['TOTAL','','','','','',recTot,'','','','','']
  sTot(s8RTot); s8RTot.getCell(7).numFmt=numFmt

  const issueStart=receipts.length+9
  sectionHdr(s8,issueStart,'STORE ISSUES (Actual Cost)')
  const s8IHdr=s8.getRow(issueStart+1)
  s8IHdr.values=['PO Number','Ref Code','Material','Sub Code','Cost Code','Unit','Qty Issued','Unit Rate','Amount (Actual)','Date','','']
  sHdr(s8IHdr,C.navyLight)
  issues.forEach((row,i)=>{
    const r=s8.getRow(issueStart+2+i)
    const tot=f(row.qty_issued)*f(row.unit_rate)
    r.values=[row.po_number||'',row.ref_code,row.material||row.material_description||row.description,row.sub_code,row.cost_code,row.unit,f(row.qty_issued),f(row.unit_rate),tot,row.issue_date||row.created_at?.slice(0,10),'','']
    sData(r,i%2===0); ;[7,8,9].forEach(ci=>r.getCell(ci).numFmt=numFmt); r.height=17
  })
  const issTot=issues.reduce((s,r)=>s+f(r.qty_issued)*f(r.unit_rate),0)
  const s8ITot=s8.getRow(issueStart+2+issues.length)
  s8ITot.values=['TOTAL','','','','','','','',issTot,'','','']
  sTot(s8ITot); s8ITot.getCell(9).numFmt=numFmt

  // ════════════════════════════════════════════════════════
  // SHEET 9 — PETTY CASH
  // ════════════════════════════════════════════════════════
  const s9=wb.addWorksheet('9-Petty Cash')
  setW(s9,[14,16,30,18,16,16,14,14,14,14,14,14])
  addTitle(s9,'PETTY CASH'); addInfo(s9,project,now)

  const s9Hdr=s9.getRow(4)
  s9Hdr.values=['Date','Voucher No.','Item Description','Sub Code','Amount Exc.VAT','Amount Inc.VAT','Cost Code Desc','','','','','']
  sHdr(s9Hdr)

  pc.forEach((row,i)=>{
    const r=s9.getRow(i+5)
    r.values=[row.date_of_voucher,row.voucher_no,row.item_desc,row.sub_code,f(row.amount_exc_vat),f(row.amount_inc_vat),row.cost_code_desc||'','','','','','']
    sData(r,i%2===0); ;[5,6].forEach(ci=>r.getCell(ci).numFmt=numFmt); r.height=17
  })
  const pcExc=pc.reduce((s,r)=>s+f(r.amount_exc_vat),0)
  const pcInc=pc.reduce((s,r)=>s+f(r.amount_inc_vat),0)
  const s9Tot=s9.getRow(pc.length+5)
  s9Tot.values=['TOTAL','','','',pcExc,pcInc,'','','','','','']
  sTot(s9Tot); ;[5,6].forEach(ci=>s9Tot.getCell(ci).numFmt=numFmt)

  // ════════════════════════════════════════════════════════
  // SHEET 10 — CASH FLOW & FINANCE
  // ════════════════════════════════════════════════════════
  const s10=wb.addWorksheet('10-Cash Flow')
  setW(s10,[14,22,30,16,14,14,14,14,14,14,14,14])
  addTitle(s10,'CASH FLOW & FINANCE'); addInfo(s10,project,now)

  // Summary block
  sectionHdr(s10,4,'CASH FLOW SUMMARY')
  const cfSum=[
    ['Cash In (Received from Client)', f(d.cashIn),           ''],
    ['Cash Out (Payments Made)',        f(d.cashOut),          ''],
    ['Net Cash Position',               f(d.cashIn-d.cashOut), d.cashIn-d.cashOut>=0?'✓ Positive':'⚠ Deficit'],
    ['','',''],
    ['Committed (Approved POs)',        f(d.committed),        `${(p(d.committed,eac)*100).toFixed(1)}% of EAC`],
    ['Invoiced to Client',              f(invoiced),           'Manual / Cash In'],
    ['Earned Value (EV)',               f(ev),                 cur],
    ['Unbilled (EV − Invoiced)',        f(unbilled),           unbilled>0?'⚠ Recover billing':'✓ OK'],
  ]
  cfSum.forEach((row,i)=>{
    const r=s10.getRow(i+5)
    r.values=[row[0],row[1],row[2],'','','','','','','','','']
    if(i===2){ sTot(r,'FF1C2B3A'); r.getCell(2).font=fnt(true,10,d.cashIn-d.cashOut>=0?'FF90EE90':'FFFF9999') }
    else if(row[0]==='') r.height=6
    else sData(r,i%2===0)
    if(typeof row[1]==='number') r.getCell(2).numFmt=numFmt
    r.height=i===3?6:17
  })

  // Cash In detail
  const ciStart=14
  sectionHdr(s10,ciStart,'CASH IN — RECEIPTS DETAIL')
  const s10CIHdr=s10.getRow(ciStart+1)
  s10CIHdr.values=['Date','Reference','Description','Amount ('+cur+')','Notes','','','','','','','']
  sHdr(s10CIHdr,C.green)
  cashIn.forEach((row,i)=>{
    const r=s10.getRow(ciStart+2+i)
    r.values=[row.date||row.receipt_date||row.created_at?.slice(0,10),row.reference||row.receipt_no,row.description||row.notes||'',f(row.collected_amount),row.notes||'','','','','','','','']
    sData(r,i%2===0); r.getCell(4).numFmt=numFmt; r.height=17
  })
  const ciTot=cashIn.reduce((s,r)=>s+f(r.collected_amount),0)
  const s10CITot=s10.getRow(ciStart+2+cashIn.length)
  s10CITot.values=['TOTAL','','',ciTot,'','','','','','','','']
  sTot(s10CITot,C.green); s10CITot.getCell(4).numFmt=numFmt

  // Finance transactions
  const ftStart=ciStart+cashIn.length+5
  sectionHdr(s10,ftStart,'FINANCE TRANSACTIONS')
  const s10FHdr=s10.getRow(ftStart+1)
  s10FHdr.values=['Date','Type','Description','Amount Inc.VAT','Cost Code','Sub Code','Reference','','','','','']
  sHdr(s10FHdr,C.navyLight)
  finance.forEach((row,i)=>{
    const r=s10.getRow(ftStart+2+i)
    r.values=[row.date||row.created_at?.slice(0,10),row.type,row.description||row.notes||'',f(row.amount_inc_vat||row.amount),row.cost_code_id||'',row.sub_code||'',row.reference||'','','','','','']
    sData(r,i%2===0); r.getCell(4).numFmt=numFmt; r.height=17
  })

  // ════════════════════════════════════════════════════════
  // SHEET 11 — FORECAST EAC
  // ════════════════════════════════════════════════════════
  const s11=wb.addWorksheet('11-Forecast EAC')
  setW(s11,[13,30,18,18,18,14,16,14,14,14,14,14])
  addTitle(s11,'EAC FORECAST REPORT'); addInfo(s11,project,now)

  // EVM Summary
  sectionHdr(s11,4,'EARNED VALUE METRICS SUMMARY')
  const evmRows=[
    ['BAC (Budget at Completion)', f(d.totalBudget),''],
    ['EV  (Earned Value)',          f(ev),''],
    ['AC  (Actual Cost)',           f(d.totalActual),''],
    ['CPI (EV÷AC)',                 f(cpi),'decimal'],
    ['EAC Forecast',               f(eac),eac>d.totalBudget?'⚠ Over Budget':'✓ Under Budget'],
    ['VAC (BAC−EAC)',               f(vac),vac>=0?'✓ Saving':'⚠ Overrun'],
    ['CTC (EAC−AC)',                f(ctc),'Remaining spend'],
  ]
  evmRows.forEach((row,i)=>{
    const r=s11.getRow(i+5)
    r.values=[row[0],row[1],row[2],'','','','','','','','','']
    sData(r,i%2===0)
    r.getCell(2).numFmt=row[2]==='decimal'?decFmt:numFmt
    if(i===5) vColor(r,2,f(vac))
    if(i===4) r.getCell(2).font=fnt(true,10,eac>d.totalBudget?C.red:C.green)
    r.height=18
  })

  // EAC by sub_code
  s11.getRow(13).height=6
  sectionHdr(s11,14,'EAC DETAIL BY SUB CODE')
  const s11Hdr=s11.getRow(15)
  s11Hdr.values=['Sub Code','Description','Budget ('+cur+')','EAC ('+cur+')','Variance','% of Budget','Method','Source','Package','','','']
  sHdr(s11Hdr)
  eacData.forEach((row,i)=>{
    const r=s11.getRow(i+16)
    const bgt=f(row.budget||0)
    const eacV=f(row.eac_amount)
    const varV=bgt-eacV
    r.values=[row.sub_code,row.description||'',bgt,eacV,varV,bgt>0?eacV/bgt:0,row.method||'manual',row.source,row.package_name||'','','','']
    sData(r,i%2===0); ;[3,4,5].forEach(ci=>r.getCell(ci).numFmt=numFmt); r.getCell(6).numFmt=pctFmt
    if(bgt>0) vColor(r,5,varV); r.height=17
  })
  const eacTotal=eacData.reduce((s,r)=>s+f(r.eac_amount),0)
  const s11Tot=s11.getRow(eacData.length+16)
  s11Tot.values=['TOTAL','',d.totalBudget,eacTotal,d.totalBudget-eacTotal,p(eacTotal,d.totalBudget),'','','','','','']
  sTot(s11Tot); ;[3,4,5].forEach(ci=>s11Tot.getCell(ci).numFmt=numFmt); s11Tot.getCell(6).numFmt=pctFmt

  // ════════════════════════════════════════════════════════
  // SHEET 12 — DIRECT BUDGET (Full)
  // ════════════════════════════════════════════════════════
  const s12=wb.addWorksheet('12-Direct Budget')
  setW(s12,[13,35,18,18,18,14,14,14,14,14,14,14])
  addTitle(s12,'DIRECT BUDGET — FULL DETAIL'); addInfo(s12,project,now)
  const s12Hdr=s12.getRow(4)
  s12Hdr.values=['Sub Code','Description / Package','Total Budget','EAC','Variance','EV Amount','% of Total','Row Type','Package','','','']
  sHdr(s12Hdr)
  budget.forEach((row,i)=>{
    const isT=(row.row_type||'').toLowerCase()==='total'
    const bac=f(row.total_budget)
    const sc=(row.sub_code||'').toLowerCase().trim()
    const eacV=eacBySub[sc]||0
    const varV=bac-eacV
    const r=s12.getRow(i+5)
    r.values=[row.sub_code,row.description||row.package_name,bac,eacV||'',eacV?varV:'',f(row.ev_amount)||'',bac>0&&d.directBudget>0?bac/d.directBudget:'',row.row_type,row.package_name,'','','']
    if(isT){ sTot(r,'FF2E3F52') } else { sData(r,i%2===0); if(!row.sub_code) sSubHdr(r) }
    ;[3,4,5,6].forEach(ci=>{ if(typeof r.getCell(ci).value==='number'&&r.getCell(ci).value!==0) r.getCell(ci).numFmt=numFmt })
    r.getCell(7).numFmt=pctFmt
    if(eacV&&bac) vColor(r,5,varV); r.height=17
  })

  // ════════════════════════════════════════════════════════
  // SHEET 13 — INDIRECT BUDGET (Full)
  // ════════════════════════════════════════════════════════
  const s13=wb.addWorksheet('13-Indirect Budget')
  setW(s13,[13,35,18,18,18,14,14,14,14,14,14,14])
  addTitle(s13,'INDIRECT BUDGET — FULL DETAIL'); addInfo(s13,project,now)
  const s13Hdr=s13.getRow(4)
  s13Hdr.values=['Sub Code','Description / Package','Total Budget','EAC','Variance','EV Amount','% of Total','Row Type','Package','','','']
  sHdr(s13Hdr)
  indirect.forEach((row,i)=>{
    const isT=(row.row_type||'').toLowerCase()==='total'
    const bac=f(row.total_budget)
    const sc=(row.sub_code||'').toLowerCase().trim()
    const eacV=eacBySub[sc]||0
    const varV=bac-eacV
    const r=s13.getRow(i+5)
    r.values=[row.sub_code,row.description||row.package_name,bac,eacV||'',eacV?varV:'',f(row.ev_amount)||'',bac>0&&d.indirectBudget>0?bac/d.indirectBudget:'',row.row_type,row.package_name,'','','']
    if(isT){ sTot(r,'FF2E3F52') } else { sData(r,i%2===0); if(!row.sub_code) sSubHdr(r) }
    ;[3,4,5,6].forEach(ci=>{ if(typeof r.getCell(ci).value==='number'&&r.getCell(ci).value!==0) r.getCell(ci).numFmt=numFmt })
    r.getCell(7).numFmt=pctFmt
    if(eacV&&bac) vColor(r,5,varV); r.height=17
  })

  // ── Generate & Download ──
  const buf=await wb.xlsx.writeBuffer()
  const blob=new Blob([buf],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
  const url=URL.createObjectURL(blob)
  const a=document.createElement('a')
  a.href=url
  a.download=`QUBEX_FullReport_${(project.name||'').replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`
  a.click(); URL.revokeObjectURL(url)
}
